import React, { useContext, useRef } from "react";
import { Form, Formik, FormikProps } from "formik";
import Grid from "@material-ui/core/Grid/Grid";
import { AxiosResponse } from "axios";
import * as Yup from "yup";
import { useHistory, useParams } from "react-router-dom";
import CustomButton from "../../../../../components/Button/CustomButton";
import ProductApi from "../../../../../api/services/ProductApi";
import { TesaPrototype } from "../../../../../api/models/product/TesaPrototype";
import PagedResponse from "../../../../../api/models/PagedResponse";
import useEnv from "../../../../../hooks/env";
import PrototypeSampleAddFormType from "../../../../../api/models/product/PrototypeSampleAddFormType";
import ErrorSnackbarContext from "../../../../../contexts/ErrorSnackbarContext";
import { ProductReferenceResponseWithType } from "../../../../../api/models/ProductReferenceResponse";
import useStyles from "./PrototypeSuggestionFormStyle";
import { PrototypeSampleAddFormProps, PrototypeSearchAutocomplete } from "./PrototypeSuggestionForm";
import { getPrototypeProductFullName } from "../../forms/productReferenceHelper";
import CustomTextField from "../../../../../components/Input/CustomTextField";
import RequestsPagePathParams from "../../../../../api/models/test-request/RequestsPagePathParams";

const createInitialValues = (preset: ProductReferenceResponseWithType | null | undefined) => {
  if (preset) {
    return {
      id: preset?.prototypeProductId,
      name: preset?.prototypeProductName,
      responsibleLaboratory: {
        id: preset?.prototypeProductResponsibleLaboratoryId,
        name: preset?.prototypeProductResponsibleLaboratoryName,
      },
      responsibleDeveloper: null,
      referringTesaProductNart: null,
      description: null,
      creationDate: null,
    } as unknown as TesaPrototype;
  }
  return null;
};

const PrototypeSampleAddFormEmployee = (props: PrototypeSampleAddFormProps) => {
  const { halfWidth, handleSubmit, hideSubmit, preset, disabled, suggestion, notFoundLink } = props;
  const { api } = useEnv();
  const productApi = new ProductApi(api);
  const prototypeSearch = useRef<HTMLDivElement & { reset: () => void }>(null);
  const classes = useStyles();
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const { testRequestId, tabName } = useParams<RequestsPagePathParams>();
  const history = useHistory();

  const yupValidationSchema = Yup.object().shape({
    prototype: Yup.mixed().required("Please select"),
  });

  const handleNavigation = () => {
    localStorage.setItem("recentReqId", testRequestId);
    localStorage.setItem("recentTab", tabName);
    localStorage.setItem("recentDialog", "");
    history.push("/products/prototype");
  };

  return (
    <Formik<PrototypeSampleAddFormType>
      initialValues={{
        prototype: createInitialValues(preset),
      }}
      onSubmit={(values) => {
        if (handleSubmit) {
          handleSubmit(values);
        }
      }}
      validationSchema={yupValidationSchema}
    >
      {(formikProps: FormikProps<PrototypeSampleAddFormType>): JSX.Element => {
        const { errors, isSubmitting, touched, values, setFieldValue } = formikProps;

        return (
          <Form>
            <Grid container justify="flex-start" direction="row" spacing={4}>
              {suggestion && (
                <Grid item xs={halfWidth ? 6 : 12}>
                  <CustomTextField
                    fullWidth
                    disabled
                    id="prototypeProductSuggestion"
                    label="Product suggested by the requestor"
                    value={suggestion}
                  />
                </Grid>
              )}
              <Grid item xs={halfWidth ? 6 : 12}>
                <PrototypeSearchAutocomplete
                  disabled={disabled}
                  ref={prototypeSearch}
                  inputLabel="Prototype Name"
                  optionFetch={(query: string): Promise<PagedResponse<TesaPrototype>> => {
                    return productApi
                      .getPrototypes(query)
                      .then((res: AxiosResponse) => {
                        return res?.data;
                      })
                      .catch(handleHttpError);
                  }}
                  onChange={(e, value): void => setFieldValue("prototype", value)}
                  mandatory
                  map={(value): { key: string; label: string } => ({
                    key: `${value.id}`,
                    label: getPrototypeProductFullName(value.name, value.responsibleLaboratory?.name),
                  })}
                  value={values.prototype}
                  error={!!(errors.prototype && touched.prototype)}
                />
              </Grid>
            </Grid>
            {notFoundLink && (
              <Grid container item direction="row" justify="flex-start">
                <span onClick={handleNavigation} className="helperTextLink" role="link" tabIndex={0}>
                  Not found? Create new tesa Prototype
                </span>
              </Grid>
            )}
            {!hideSubmit && (
              <Grid container item direction="row" justify="flex-end">
                <CustomButton
                  className={classes.marginTop}
                  disabled={isSubmitting}
                  primary
                  text="Use Prototype"
                  type="submit"
                />
              </Grid>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default PrototypeSampleAddFormEmployee;
